// integrations/alm/alm-masonry.js

/**
 * Theme-owned Masonry layout for Ajax Load More archives.
 *
 * ALM's built-in Masonry add-on throws on Barba SPA re-initialisation — its
 * `new Masonry()` runs inside an imagesLoaded callback wired to DOMContentLoaded,
 * which never re-fires after the first page load — leaving the listing collapsed
 * (wrapper stuck at opacity 0, items unpositioned). ALM's masonry transition is
 * therefore disabled server-side (see archive-results.php) and the theme lays out
 * the grid itself here, uniformly on initial load and every transition.
 *
 * Listings are marked with `.esr-masonry` (via the shortcode's css_classes) and
 * hold items matching `.item`. A MutationObserver re-lays-out the grid whenever
 * ALM appends (load more) or replaces (filter) items.
 */

import Masonry from 'masonry-layout';
import imagesLoaded from 'imagesloaded';

const SELECTOR = '.alm-listing.esr-masonry';

// Per-listing state: { masonry, observer, debounce }. Keyed by the listing element.
const instances = new WeakMap();

/**
 * Reveal a listing (and any masonry wrapper ALM may have left hidden).
 * @param {HTMLElement} listing - The `.alm-listing.esr-masonry` element.
 * @return {void}
 */
function reveal(listing) {
  listing.style.opacity = '1';
  const wrapper = listing.closest('.alm-masonry');
  if (wrapper) wrapper.style.opacity = '1';
}

/**
 * Re-measure and lay out a listing's items once its images have loaded.
 * @param {HTMLElement} listing - The `.alm-listing.esr-masonry` element.
 * @param {boolean} [revealAfter=false] - Reveal the listing once laid out (initial run).
 * @return {void}
 */
function layout(listing, revealAfter = false) {
  imagesLoaded(listing, () => {
    const rec = instances.get(listing);
    if (!rec || !rec.masonry) return;
    rec.masonry.reloadItems();
    rec.masonry.layout();
    if (revealAfter) reveal(listing);
  });
}

/**
 * Initialise Masonry on a single listing and watch it for content changes.
 * @param {HTMLElement} listing - The `.alm-listing.esr-masonry` element.
 * @return {void}
 */
function initOne(listing) {
  if (instances.get(listing)) return; // already initialised

  const masonry = new Masonry(listing, {
    itemSelector: '.item',
    columnWidth: '.item',
    percentPosition: true,
    transitionDuration: 0,
  });

  const rec = { masonry, observer: null, debounce: 0 };
  instances.set(listing, rec);

  // Hide until the first layout completes to avoid a flash of ungridded items.
  // Set via JS (not CSS) so content is never left hidden if the script fails.
  listing.style.opacity = '0';
  layout(listing, true);

  // Re-layout on ALM append (load more) or replace (filter). Only childList is
  // observed, so Masonry's own inline-style writes don't retrigger it.
  rec.observer = new MutationObserver(() => {
    clearTimeout(rec.debounce);
    rec.debounce = setTimeout(() => layout(listing), 100);
  });
  rec.observer.observe(listing, { childList: true });
}

/**
 * Initialise theme Masonry for all matching listings within a container.
 * @param {HTMLElement|Document} [container=document] - Scope to search.
 * @return {void}
 */
export function initAlmMasonry(container = document) {
  (container || document).querySelectorAll(SELECTOR).forEach(initOne);
}

/**
 * Tear down theme Masonry for all matching listings within a container.
 * @param {HTMLElement|Document} [container=document] - Scope to search.
 * @return {void}
 */
export function destroyAlmMasonry(container = document) {
  (container || document).querySelectorAll(SELECTOR).forEach((listing) => {
    const rec = instances.get(listing);
    if (!rec) return;
    clearTimeout(rec.debounce);
    if (rec.observer) rec.observer.disconnect();
    if (rec.masonry && rec.masonry.destroy) rec.masonry.destroy();
    instances.delete(listing);
  });
}
